/**
 * @fileoverview Report page that reuses the Report Modal component
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import ReportModal from '../../components/modals/ReportModal';
import { AlertTriangle, Bug, Shield, MessageSquare } from 'lucide-react';
import { HelpPageLayout } from '../../components/help/HelpPageLayout';

const Report: React.FC = () => {
  const { t } = useTranslation();
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const reportTypes = [
    {
      icon: <Bug className="w-6 h-6" />,
      title: "Bug Report",
      description: "Report technical issues, glitches, or app malfunctions",
      examples: [
        "App crashes when spinning",
        "Buttons not responding",
        "Display issues",
        "Performance problems"
      ]
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Content Issue",
      description: "Report inappropriate, offensive, or incorrect content",
      examples: [
        "Inappropriate questions",
        "Offensive language",
        "Incorrect information",
        "Spam or irrelevant content"
      ]
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Feature Request",
      description: "Suggest new features or improvements",
      examples: [
        "New game types",
        "Additional customization options",
        "Performance improvements",
        "User interface enhancements"
      ]
    },
    {
      icon: <AlertTriangle className="w-6 h-6" />,
      title: "Other Issues",
      description: "Report any other problems or concerns",
      examples: [
        "Account issues",
        "Privacy concerns",
        "Accessibility problems",
        "General feedback"
      ]
    }
  ];

  const reportGuidelines = [
    "Be specific and provide detailed information about the issue",
    "Include steps to reproduce the problem if applicable",
    "Mention your device type and browser version",
    "Provide screenshots if possible",
    "Be respectful and constructive in your feedback"
  ];

  return (
    <HelpPageLayout>
      <Helmet>
        <title>Report Issues - Spinner Games !!</title>
        <meta name="description" content="Report bugs, inappropriate content, or suggest improvements for our spinner app." />
                  <meta property="og:title" content="Report Issues - Spinner Games !!" />
        <meta property="og:description" content="Help us improve by reporting issues or inappropriate content." />
        <link rel="canonical" href="https://spinnergames.fun/help/report" />
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Report Issues",
          "description": "Report bugs, inappropriate content, or suggest improvements",
          "mainEntity": {
            "@type": "Organization",
            "name": "Spinner Games !!",
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "email": "support@spinnergames.fun"
            }
          }
        })}
        </script>
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-10 h-10 text-red-600 dark:text-red-300" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Report Issues
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Help us improve by reporting bugs, inappropriate content, or suggesting new features
          </p>
        </div>

        {/* Report Types */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What Would You Like to Report?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {reportTypes.map((type, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center text-red-600 dark:text-red-300">
                    {type.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      {type.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      {type.description}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-900 dark:text-white">Examples:</h4>
                  <ul className="space-y-1">
                    {type.examples.map((example, exampleIndex) => (
                      <li key={exampleIndex} className="text-sm text-gray-600 dark:text-gray-300">
                        • {example}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Report Guidelines */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-8 mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Reporting Guidelines
          </h2>
          <div className="space-y-4">
            {reportGuidelines.map((guideline, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">
                  {index + 1}
                </div>
                <p className="text-gray-700 dark:text-gray-300">
                  {guideline}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Report Button */}
        <div className="text-center mb-16">
          <button
            onClick={() => setIsReportModalOpen(true)}
            className="inline-flex items-center px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors text-lg"
          >
            <AlertTriangle className="w-6 h-6 mr-3" />
            Submit Report
          </button>
          <p className="text-gray-600 dark:text-gray-300 mt-4">
            Click the button above to open our report form
          </p>
        </div>

        {/* Alternative Contact */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Alternative Ways to Report
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Email Support
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-2">
                Send us an email directly for urgent issues:
              </p>
              <a
                href="mailto:support@spinnergames.fun"
                className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
              >
                support@spinnergames.fun
              </a>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Contact Form
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-2">
                Use our general contact form for non-urgent issues:
              </p>
              <a
                href="/help/contact"
                className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>

        {/* Response Time */}
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Response Time
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            We typically respond to reports within 24-48 hours during business days.
          </p>
          <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
            <p><strong>Critical Issues:</strong> Within 24 hours</p>
            <p><strong>Content Issues:</strong> Within 48 hours</p>
            <p><strong>Feature Requests:</strong> Within 1 week</p>
          </div>
        </div>

        {/* Back to Help */}
        <div className="text-center mt-12">
          <a
            href="/help"
            className="inline-flex items-center px-6 py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors"
          >
            Back to Help Center
          </a>
        </div>
      </div>

      {/* Report Modal */}
      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        gameType="general"
        questionId="help-report"
        questionText="General help report from user"
        onReportSubmitted={() => setIsReportModalOpen(false)}
      />
    </HelpPageLayout>
  );
};

export default Report; 